import { useRef, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { SegmentedMessage } from 'sms-segments-calculator';
import { useToast } from '@/hooks/use-toast';
import { SMS_ENCODING, SEGMENT_SIZE, BITS_PER_CHAR, DEFAULT_METRICS } from './sms-constants';

export interface SMSMetrics {
  charactersLeft: number;
  totalSegments: number;
  encoding: 'GSM' | 'Unicode';
  segmentSize: number;
  usedCharacters: number;
}

export function getSMSMetrics(text: string): SMSMetrics {
  const message = new SegmentedMessage(text);
  const isGSM = message.encodingName === SMS_ENCODING.GSM;
  const segmentSize = isGSM ? SEGMENT_SIZE.GSM : SEGMENT_SIZE.UNICODE;
  const totalSegments = message.segmentsCount;

  // Get the current segment's character count
  const currentSegmentIndex = totalSegments - 1;
  const currentSegment = message.segments[currentSegmentIndex];
  const usedCharacters =
    currentSegment.messageSizeInBits() / (isGSM ? BITS_PER_CHAR.GSM : BITS_PER_CHAR.UNICODE);

  return {
    charactersLeft: segmentSize - Math.floor(usedCharacters),
    totalSegments,
    encoding: isGSM ? 'GSM' : 'Unicode',
    segmentSize,
    usedCharacters: Math.floor(usedCharacters)
  };
}

export function useSMSMetrics() {
  const metricsRef = useRef<SMSMetrics>(DEFAULT_METRICS);
  const [smsMetrics, setSMSMetrics] = useState<SMSMetrics>(DEFAULT_METRICS);
  const previousEncodingRef = useRef<'GSM' | 'Unicode'>('GSM');
  const { toast } = useToast();

  // Debounced metrics calculation
  const debouncedSetMetrics = useMemo(
    () =>
      debounce((text: string) => {
        const metrics = getSMSMetrics(text);

        // Check if encoding has changed from GSM to Unicode
        if (metrics.encoding === 'Unicode' && previousEncodingRef.current === 'GSM') {
          toast({
            title: 'Character Limit Reduced',
            description:
              'Your message now uses an emoji or special character that reduces the character limit per segment from 160 to 70.',
            variant: 'default'
          });
        }

        previousEncodingRef.current = metrics.encoding;
        metricsRef.current = metrics;
        setSMSMetrics(metrics);
      }, 50),
    [toast]
  );

  return {
    metrics: smsMetrics,
    updateMetrics: debouncedSetMetrics
  };
}
